import { uniqueId } from '@ember/helper';
import { on } from '@ember/modifier';
import type { TypedRecordInstance } from '@warp-drive/core/types/record';
import type { WithLegacy } from '@warp-drive/legacy/model/migration-support';

import { EditRecordBase } from './edit-record-base.gts';
import { ValidationMessage } from './validation.gts';

export function initialState(item: { id: string | null }): 'readonly' | 'edit' {
  if (item.id === null) {
    return 'edit';
  } else {
    return 'readonly';
  }
}

export class EditRecordSidebar<
  T extends WithLegacy<TypedRecordInstance>,
> extends EditRecordBase<
  T,
  object,
  {
    sidebar: [];
  }
> {
  <template>
    {{#let (uniqueId) as |uniqueId|}}
      <form novalidate {{on "submit" this.submitClicked}} ...attributes>
        <div class="row g-4">
          <aside class="col-12 col-lg-4 col-xl-3">
            {{! sticky lives on a wrapper: .card sets position relative and
                  wins over .sticky-lg-top in this theme's import order }}
            <div class="sticky-lg-top py-lg-3 mh-lg-100dvh overflow-y-auto">
              <div class="card border shadow-sm py-3 py-lg-4 px-0">
                <div class="px-3 px-lg-4">
                  {{yield to="sidebar"}}
                </div>

                <fieldset disabled={{this.saving}}>
                  {{#if this.readonly}}
                    {{#unless @hideValidationMessage}}
                      <ValidationMessage @validation={{this.validation}} />
                    {{/unless}}

                    <div class="d-flex justify-content-center gap-2">
                      {{#if this.canEdit}}
                        <button
                          type="button"
                          class="btn btn-primary {{this.buttonSize}}"
                          {{on "click" this.editClicked}}
                          disabled={{@context.isEditing}}
                        >{{@editLabel}}</button>
                      {{/if}}
                      {{yield
                        uniqueId
                        this.readonly
                        this.validation
                        to="buttons"
                      }}
                    </div>
                  {{else}}
                    {{#unless @hideValidationMessage}}
                      <ValidationMessage @validation={{this.validation}} />
                    {{/unless}}

                    {{#if (has-block "buttons")}}
                      <div
                        class="d-flex justify-content-between gap-2"
                        {{this.interceptTransition}}
                      >
                        <div class="d-flex justify-content-center gap-2">
                          {{yield
                            uniqueId
                            this.readonly
                            this.validation
                            to="buttons"
                          }}
                        </div>
                        <div class="d-flex justify-content-center gap-2">
                          <button
                            type="button"
                            class="btn btn-outline-primary {{this.buttonSize}}"
                            {{on "click" this.cancelClicked}}
                          >{{this.cancelLabel}}</button>
                          <button
                            type="submit"
                            class="btn btn-primary {{this.buttonSize}}"
                          >{{this.saveLabel}}</button>
                        </div>
                      </div>
                    {{else}}
                      <div
                        class="d-flex justify-content-center gap-2"
                        {{this.interceptTransition}}
                      >
                        <button
                          type="button"
                          class="btn btn-outline-primary {{this.buttonSize}}"
                          {{on "click" this.cancelClicked}}
                        >{{this.cancelLabel}}</button>
                        <button
                          type="submit"
                          class="btn btn-primary {{this.buttonSize}}"
                        >{{this.saveLabel}}</button>
                      </div>
                    {{/if}}
                  {{/if}}
                </fieldset>
              </div>
            </div>
          </aside>
          <div class="col-12 col-lg-8 col-xl-9">
            {{yield
              uniqueId
              this.isDisabled
              this.readonly
              this.validation
              this.cancelClicked
              to="form"
            }}
          </div>
        </div>
      </form>
    {{/let}}
  </template>
}
